import type { Stroke } from '../types/app.js';
import type { DataGeometry, DataLayer } from './data-layer.js';
import { visibleDataFeatures } from './data-layer-display.js';
import { dataFeatureStyle } from './data-layer-fields.js';
import { DEFAULT_DRAWING_LAYER, strokeDrawingLayerId, visibleStrokes, type DrawingLayer } from './drawing-layer.js';
import { collectDrawingLegend, legendSymbolKey, type DrawingLegendItem, type LegendSymbol } from './drawing-legend.js';

interface LegendSource {
  strokes: Stroke[];
  drawingLayers?: DrawingLayer[];
  dataLayers?: DataLayer[];
  showLayerLegend?: boolean;
}

function distinctSymbols(strokes: Stroke[]): Stroke[] {
  return [...new Map(strokes.map(stroke => [legendSymbolKey(stroke), stroke])).values()];
}

function geometryTools(geometry: DataGeometry | null): Array<'marker' | 'line' | 'polygon'> {
  if (!geometry) return [];
  if (geometry.type === 'GeometryCollection') return geometry.geometries.flatMap(geometryTools);
  if (geometry.type.includes('Point')) return ['marker'];
  return [geometry.type.includes('Polygon') ? 'polygon' : 'line'];
}

function dataSymbols(layer: DataLayer): LegendSymbol[] {
  const symbols = new Map<string, LegendSymbol>();
  for (const feature of visibleDataFeatures(layer)) {
    const style = dataFeatureStyle(feature, layer);
    if (style.opacity === 0) continue;
    for (const tool of geometryTools(feature.geometry)) {
      const symbol: LegendSymbol = { id: feature.id, tool, points: [], zoom: 10, zoomScale: false, color: style.color, width: style.width ?? 3,
        opacity: style.opacity, fillColor: style.fillColor, fillOpacity: style.fillOpacity,
        lineStyle: feature.properties.lineStyle === 'dashed' ? 'dashed' : 'solid' };
      if (tool === 'marker') symbol.dataPoint = style.icon ?? true;
      symbols.set(JSON.stringify([legendSymbolKey(symbol), symbol.dataPoint]), symbol);
    }
  }
  if (!layer.features.length && layer.dataset?.count && layer.style.opacity !== 0) {
    const colors = layer.display?.colorBy?.categories.map(category => category.color) ?? [layer.style.color];
    return [...new Set(colors)].map(color => ({ id: layer.id, tool: 'polygon', points: [], zoom: 10, zoomScale: false,
      color, width: 1, opacity: layer.style.opacity, layerSwatch: true }));
  }
  return [...symbols.values()];
}

/** Derive live layer entries without changing custom items or map geometry. */
export function collectMapLegend(source: LegendSource): DrawingLegendItem[] {
  const layers = source.drawingLayers ?? [DEFAULT_DRAWING_LAYER];
  const strokes = visibleStrokes(source.strokes, layers).filter(stroke => stroke.tool !== 'text' && stroke.opacity !== 0);
  if (!source.showLayerLegend) return collectDrawingLegend(strokes);
  const entries: DrawingLegendItem[] = [];
  for (const layer of [...layers].sort((a, b) => a.position - b.position)) {
    const members = strokes.filter(stroke => strokeDrawingLayerId(stroke) === layer.id);
    if (!members.length) continue;
    entries.push({ id: `drawing-layer:${layer.id}`, label: layer.name, order: entries.length, layer: true, strokes: members, symbols: distinctSymbols(members) });
  }
  for (const layer of [...source.dataLayers ?? []].sort((a, b) => a.position - b.position)) {
    if (!layer.visible) continue;
    const symbols = dataSymbols(layer);
    if (symbols.length) entries.push({ id: `data-layer:${layer.id}`, label: layer.name, order: entries.length, layer: true, strokes: [], symbols });
  }
  return entries;
}
